import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { FamilyKey, FamilyMemberSelection } from "../types/taxonomy";
import type { ReviewFilters } from "../types/review";

/**
 * Bulk selection store
 *
 * Holds the set of selected image IDs, a snapshot of the filters at the time
 * of selection (so we can detect "hidden selection" when filters change), and
 * the staged draft changes for the bulk review panel.
 *
 * Design notes:
 * - Selection survives filter changes — we DO NOT clear ids when filters change.
 *   Instead, we expose `filterSnapshot` so the UI can compute
 *   `loaded` vs `not in current view` honestly.
 * - `drafts` holds the intended member index for each family. `none` = no change.
 * - `dumpImageValidOverride` is a tri-state: undefined = no change, true/false = apply.
 *
 * Persistence: a reviewer who has marqueed 300 frames and half-staged a bulk
 * apply loses all of it to an accidental reload, which is the single most
 * expensive thing that can happen on this page. The selection is therefore
 * written to localStorage — but it is NEVER silently restored. On reload the
 * saved work lands in `resumable` and the page offers Restore or Discard,
 * because restoring a selection into a scope or time window it wasn't made
 * for would be worse than losing it: the reviewer would bulk-apply to frames
 * they never looked at.
 */

/** Identity of the view a selection was made in. */
export interface SelectionScopeSignature {
  locationId: number | undefined;
  machineIds: number[];
  fromDate: string;
  toDate: string;
}

/** A selection recovered from storage, awaiting Restore or Discard. */
export interface ResumableSelection {
  ids: number[];
  filterSnapshot: ReviewFilters | null;
  drafts: Partial<Record<FamilyKey, FamilyMemberSelection>>;
  dumpImageValidOverride: boolean | undefined;
  scope: SelectionScopeSignature | null;
  /** Epoch ms the selection was last written. Drives the banner's age copy. */
  savedAt: number;
}

interface BulkSelectionState {
  ids: Set<number>;
  filterSnapshot: ReviewFilters | null;
  drafts: Partial<Record<FamilyKey, FamilyMemberSelection>>;
  dumpImageValidOverride: boolean | undefined;
  /** Scope the live selection belongs to; persisted alongside it. */
  scope: SelectionScopeSignature | null;
  savedAt: number;
  /** Recovered-from-storage selection the user hasn't accepted yet. */
  resumable: ResumableSelection | null;
}

interface BulkSelectionActions {
  toggle: (imageId: number) => void;
  add: (imageId: number) => void;
  addMany: (ids: number[]) => void;
  remove: (imageId: number) => void;
  clear: () => void;
  captureFilterSnapshot: (snapshot: ReviewFilters) => void;
  captureScope: (scope: SelectionScopeSignature) => void;
  stageFamily: (family: FamilyKey, selection: FamilyMemberSelection) => void;
  clearDrafts: () => void;
  setDumpImageValid: (value: boolean | undefined) => void;
  resetAll: () => void;
  restoreResumable: () => void;
  discardResumable: () => void;
}

export type BulkSelectionStore = BulkSelectionState & BulkSelectionActions;

const initialState: BulkSelectionState = {
  ids: new Set(),
  filterSnapshot: null,
  drafts: {},
  dumpImageValidOverride: undefined,
  scope: null,
  savedAt: 0,
  resumable: null,
};

/** True when two scope signatures describe the same view. */
export const sameScope = (
  a: SelectionScopeSignature | null,
  b: SelectionScopeSignature | null,
): boolean => {
  if (!a || !b) return false;
  return (
    a.locationId === b.locationId &&
    a.fromDate === b.fromDate &&
    a.toDate === b.toDate &&
    a.machineIds.length === b.machineIds.length &&
    a.machineIds.every((id, i) => id === b.machineIds[i])
  );
};

export const useBulkSelectionStore = create<BulkSelectionStore>()(
  persist(
    (set) => ({
      ...initialState,

      toggle: (imageId) =>
        set((state) => {
          const next = new Set(state.ids);
          if (next.has(imageId)) next.delete(imageId);
          else next.add(imageId);
          return { ids: next, savedAt: Date.now() };
        }),

      add: (imageId) =>
        set((state) => {
          if (state.ids.has(imageId)) return state;
          const next = new Set(state.ids);
          next.add(imageId);
          return { ids: next, savedAt: Date.now() };
        }),

      addMany: (ids) =>
        set((state) => {
          const next = new Set(state.ids);
          for (const id of ids) next.add(id);
          return { ids: next, savedAt: Date.now() };
        }),

      remove: (imageId) =>
        set((state) => {
          if (!state.ids.has(imageId)) return state;
          const next = new Set(state.ids);
          next.delete(imageId);
          return { ids: next, savedAt: Date.now() };
        }),

      clear: () =>
        set({
          ids: new Set(),
          drafts: {},
          dumpImageValidOverride: undefined,
        }),

      captureFilterSnapshot: (snapshot) => set({ filterSnapshot: snapshot }),

      captureScope: (scope) => set({ scope }),

      stageFamily: (family, selection) =>
        set((state) => {
          const drafts = { ...state.drafts };
          if (selection === "none") delete drafts[family];
          else drafts[family] = selection;
          return { drafts, savedAt: Date.now() };
        }),

      clearDrafts: () =>
        set({ drafts: {}, dumpImageValidOverride: undefined }),

      setDumpImageValid: (value) =>
        set({ dumpImageValidOverride: value, savedAt: Date.now() }),

      resetAll: () => set({ ...initialState, ids: new Set() }),

      restoreResumable: () =>
        set((state) => {
          const r = state.resumable;
          if (!r) return state;
          return {
            ids: new Set(r.ids),
            filterSnapshot: r.filterSnapshot,
            drafts: r.drafts,
            dumpImageValidOverride: r.dumpImageValidOverride,
            scope: r.scope,
            savedAt: Date.now(),
            resumable: null,
          };
        }),

      discardResumable: () => set({ resumable: null }),
    }),
    {
      name: "ml-review-selection",
      storage: createJSONStorage(() => localStorage),
      // `ids` is a Set, which JSON drops silently — persist it as an array and
      // rebuild on the way back in (see merge).
      partialize: (state) => ({
        ids: [...state.ids],
        filterSnapshot: state.filterSnapshot,
        drafts: state.drafts,
        dumpImageValidOverride: state.dumpImageValidOverride,
        scope: state.scope,
        savedAt: state.savedAt,
      }),
      /**
       * Rehydrate into `resumable`, NOT into the live selection. The page shows
       * a banner and the reviewer decides — see the store header for why this
       * must never be automatic.
       */
      merge: (persisted, current) => {
        const p = persisted as Partial<ResumableSelection> | undefined;
        if (!p?.ids?.length) return current;
        return {
          ...current,
          resumable: {
            ids: p.ids,
            filterSnapshot: p.filterSnapshot ?? null,
            drafts: p.drafts ?? {},
            dumpImageValidOverride: p.dumpImageValidOverride,
            scope: p.scope ?? null,
            savedAt: p.savedAt ?? 0,
          },
        };
      },
    },
  ),
);

/** Count of staged draft changes (families + dump_image_valid). */
export const countStagedChanges = (s: BulkSelectionState): number => {
  return (
    Object.keys(s.drafts).length +
    (s.dumpImageValidOverride === undefined ? 0 : 1)
  );
};
